// Utilidades para cálculos automáticos de actividades económicas
import { hoyEnChile } from './dateHelpers';
import { parseDateOnly } from './formatters';

/**
 * Calcula el estado automático de una actividad según sus fechas
 * @param fechaInicio - Fecha de inicio de la actividad (YYYY-MM-DD) o string vacío
 * @param fechaFin - Fecha de fin de la actividad (YYYY-MM-DD) o string vacío
 * @returns Estado automático: 'planificacion' | 'en_curso' | 'finalizada'
 */
export function calcularEstadoAutomatico(
  fechaInicio: string,
  fechaFin: string
): 'planificacion' | 'en_curso' | 'finalizada' {
  // Si no hay fechas definidas, se mantiene en planificación
  if (!fechaInicio || !fechaFin) {
    return 'planificacion';
  }
  
  // Días civiles 'YYYY-MM-DD' comparados como texto: "hoy" es el día de Chile, no el del navegador
  const hoy = hoyEnChile();
  const inicio = fechaInicio.slice(0, 10);
  const fin = fechaFin.slice(0, 10);
  
  // Si la actividad no ha comenzado
  if (hoy < inicio) {
    return 'planificacion';
  }
  
  // Si la actividad ya finalizó
  if (hoy > fin) {
    return 'finalizada';
  }
  
  // Si está en curso
  return 'en_curso';
}

/**
 * Calcula la utilidad objetivo dinámica según el tiempo transcurrido
 * @param utilidadObjetivo - Utilidad objetivo total de la actividad
 * @param fechaInicio - Fecha de inicio de la actividad (YYYY-MM-DD)
 * @param fechaFin - Fecha de fin de la actividad (YYYY-MM-DD)
 * @returns Utilidad dinámica calculada según tiempo transcurrido
 */
export function calcularUtilidadDinamica(
  utilidadObjetivo: number,
  fechaInicio: string,
  fechaFin: string
): number {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  
  const inicio = parseDateOnly(fechaInicio);
  inicio.setHours(0, 0, 0, 0);
  
  const fin = parseDateOnly(fechaFin);
  fin.setHours(0, 0, 0, 0);
  
  // Si la actividad no ha comenzado, utilidad es 0
  if (hoy < inicio) {
    return 0;
  }
  
  // Calcular días totales de la actividad
  const diasTotales = Math.floor((fin.getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  
  // Calcular días transcurridos
  let diasTranscurridos: number;
  if (hoy > fin) {
    diasTranscurridos = diasTotales;
  } else {
    diasTranscurridos = Math.floor((hoy.getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  }
  
  // Calcular porcentaje de tiempo transcurrido
  const porcentajeTiempo = diasTranscurridos / diasTotales;
  
  // Calcular utilidad dinámica
  const utilidadDinamica = utilidadObjetivo * porcentajeTiempo;
  
  // Redondear a entero
  return Math.round(utilidadDinamica);
}

/**
 * Obtiene el color del badge según el estado de la actividad
 * @param estado - Estado de la actividad
 * @returns Clases de color para el badge
 */
export function getColorEstado(estado: 'planificacion' | 'en_curso' | 'finalizada'): string {
  switch (estado) {
    case 'planificacion':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'en_curso':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'finalizada':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
}

/**
 * Obtiene el texto del estado en español
 * @param estado - Estado de la actividad
 * @returns Texto del estado en español
 */
export function getTextoEstado(estado: 'planificacion' | 'en_curso' | 'finalizada'): string {
  switch (estado) {
    case 'planificacion':
      return 'Planificación';
    case 'en_curso':
      return 'En Curso';
    case 'finalizada':
      return 'Finalizada';
    default:
      return 'Desconocido';
  }
}
